import { httpLink } from '@trpc/client';
import { getApiBaseUrl } from '@ptml/root-config';
import { QueryClient, focusManager } from '@tanstack/react-query';
import { SERVER_RESPONSE_CODES } from '@repairer-frontend/http-constants';

// Override React Query's default focus detection so that `refetchOnWindowFocus`
// also fires when the window receives focus (e.g. switching between browsers on
// separate monitors). The default implementation only listens to
// `visibilitychange`, which does not fire when both windows remain visible.
if (typeof window !== 'undefined' && window.addEventListener) {
    focusManager.setEventListener((handleFocus) => {
        const onVisibilityChange = () => handleFocus();
        const onFocus = () => handleFocus();

        window.addEventListener('visibilitychange', onVisibilityChange, false);
        window.addEventListener('focus', onFocus, false);

        return () => {
            window.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('focus', onFocus);
        };
    });
}

type RequestScopedHeaders = Record<string, string>;

type TrpcOperationContext = {
    headers?: RequestScopedHeaders;
};

const redirectToLoginOnAuthErrors = (httpStatus: number) => {
    if (httpStatus === SERVER_RESPONSE_CODES.UNAUTHORIZED) {
        window.location.href = `${getApiBaseUrl()}/fss/repairer/auth/login?returnUrl=${window.location.href}`;
    }
};

export const trpcFetchPolyfill = async (
    url: RequestInfo | URL,
    options?: RequestInit | undefined,
) => {
    const res = await fetch(url, {
        ...options,
        credentials: 'include',
    });
    redirectToLoginOnAuthErrors(res.status);
    return res;
};

const ONE_DAY = 86400000;

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            gcTime: ONE_DAY,
            retryOnMount: false,
        },
        mutations: {
            retry: false,
        },
    },
});

export const generateTrpcClientOptions = (mfe: string) => {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/fss/repairer/bff/${mfe}`;

    return {
        links: [
            httpLink({
                url,
                fetch: trpcFetchPolyfill,
                headers(options) {
                    const requestScopedHeaders =
                        (
                            options?.op?.context as
                                | TrpcOperationContext
                                | undefined
                        )?.headers ?? {};

                    return {
                        // Required header for the FSS
                        'x-csrf': '1',
                        ...requestScopedHeaders,
                    };
                },
            }),
        ],
    };
};
